import type { ProviderType } from '@/components/Checkout/types';
import { withAuth } from '@/utilities/queries';
import { getUacCheck, PlanType } from '@simplywallst/services';
import { useQuery } from 'react-query';

export const queryKey = 'USER_ACCESS';

export interface AccessState {
  readonly role: string;
  readonly plan: string;
  readonly planType: PlanType;
  readonly provider: ProviderType;
  readonly createPortfolios: number;
  readonly viewCompaniesInView: number;
  readonly viewCompanyReports: number;
  readonly companiesAnalysedInPortfolio: number;
  readonly canGetCompanyReportPdf: boolean;
  readonly canGetCompanyReportCsv: boolean;
  readonly canGetPortfolioReportCsv: boolean;
  readonly canSaveScreener: boolean;
  readonly canDistributeCompanyReportPdf: boolean;
  readonly canHaveProfileImage: boolean;
}
export const getDefaultData = (): AccessState => ({
  role: '',
  plan: '',
  planType: PlanType.None,
  provider: 'stripe',
  companiesAnalysedInPortfolio: -1,
  createPortfolios: -1,
  viewCompaniesInView: -1,
  viewCompanyReports: -1,
  canGetCompanyReportPdf: false,
  canGetCompanyReportCsv: false,
  canGetPortfolioReportCsv: false,
  canSaveScreener: false,
  canDistributeCompanyReportPdf: false,
  canHaveProfileImage: false,
});

async function queryFn() {
  try {
    const response = await withAuth(getUacCheck);
    const [uac] = response.data.data;
    return uac;
  } catch {
    return undefined;
  }
}

export function useUserAccess() {
  const { data, status } = useQuery(queryKey, queryFn, {
    staleTime: Infinity,
    select: (response) => {
      if (typeof response === 'undefined') {
        return getDefaultData();
      }
      return {
        role: response.role,
        plan: response.plan,
        planType: response.plan_type,
        provider: response.provider,
        companiesAnalysedInPortfolio: response.companies_analysis,
        viewCompaniesInView: response.view_companies_in_view,
        viewCompanyReports: response.view_company_reports,
        createPortfolios: response.create_portfolios,
        canGetCompanyReportPdf:
          response.company_pdf_printing === -1 ||
          response.company_pdf_printing === 1,
        canGetCompanyReportCsv:
          response.company_csv_download === -1 ||
          response.company_csv_download === 1,
        canDistributeCompanyReportPdf:
          response.company_pdf_distribution === -1 ||
          response.company_pdf_distribution === 1,
        canSaveScreener: response.save_view === -1,
        canGetPortfolioReportCsv:
          response.user_portfolio_analysis_csv === -1 ||
          response.user_portfolio_analysis_csv === 1,
        canHaveProfileImage:
          response.profile_image === -1 || response.profile_image === 1,
      };
    },
  });
  return {
    data: data || getDefaultData(),
    status,
  };
}
